import { AnimatePresence, motion } from 'motion/react';
import { PropsWithChildren, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { PhoneToastContext } from '~/routes/remote-mic/components/phone-toast-context';

// A confirmation the phone owes the person holding it - "queued", "connected as Player 2", or the
// reason a press did nothing. It rises above the tab bar rather than over the panel, so it never
// covers the control that was just pressed.

const VISIBLE_MS = 1900;

export function PhoneToastProvider({ children }: PropsWithChildren) {
  const [message, setMessage] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  const flash = useCallback((next: string) => {
    clearTimeout(timer.current);
    setMessage(next);
    timer.current = setTimeout(() => setMessage(null), VISIBLE_MS);
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  const value = useMemo(() => flash, [flash]);

  return (
    <PhoneToastContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {message && (
          <motion.div
            data-test="toast"
            role="status"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            // Fixed rather than absolute, and bounded to the same width the phone shell is: the
            // toast has to clear the tab bar whatever tree it is rendered from, and a provider has
            // no positioned ancestor to hang off.
            className="pointer-events-none fixed inset-x-0 bottom-21.5 z-[20010] mx-auto w-full max-w-[45rem] px-5">
            <div className="rounded-[0.875rem] bg-[#1e2230] px-4 py-3.5 text-center text-[0.9375rem] text-white shadow-[0_0.625rem_1.875rem_rgba(0,0,0,0.5),inset_0_0_0_1px_rgba(255,255,255,0.14)]">
              {message}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </PhoneToastContext.Provider>
  );
}
